import React, { memo } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";

/**
 * URL policy for Inky's markdown. Inky reads untrusted content (meeting transcripts, uploaded
 * files, other members' requirements), so a prompt-injected reply must not be able to make the
 * browser fetch anything on its own: images are never loaded (see {@link MarkdownImage}) and
 * links keep react-markdown's safe-protocol filter (no javascript:, no data:).
 * @param {string} url - Raw URL.
 * @returns {string} Safe URL, or "" to drop it.
 */
const inkyUrlTransform = (url) => {
  const safe = defaultUrlTransform(url);
  if (!safe) return "";
  const value = String(safe).trim();
  if (value.startsWith("//")) return "";
  if (value.startsWith("/") || value.startsWith("#") || value.startsWith("?")) return value;
  return /^(https?:|mailto:)/i.test(value) ? value : "";
};

/**
 * Link renderer for markdown: in-app paths use React Router; external links open in a new tab.
 * @param {object} props - Props from react-markdown.
 * @returns {JSX.Element} Link element.
 */
function MarkdownLink({ href, children, node: _node, ...rest }) {
  if (!href) return <span {...rest}>{children}</span>;
  if (href.startsWith("/")) {
    return (
      <Link component={RouterLink} to={href} {...rest}>
        {children}
      </Link>
    );
  }
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (origin && href.startsWith(origin)) {
      const url = new URL(href);
      return (
        <Link component={RouterLink} to={`${url.pathname}${url.search}`} {...rest}>
          {children}
        </Link>
      );
    }
  } catch {
    // fall through to external
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </Link>
  );
}

/**
 * Image renderer for markdown: shows a link with the image's description instead of loading it.
 * A markdown image in a reply would otherwise be fetched the moment the reply renders, which a
 * prompt-injected instruction could use to send data (in the URL) to any server, unclicked.
 * @param {object} props - Props from react-markdown.
 * @returns {JSX.Element} Text link (nothing is fetched until the user clicks it).
 */
function MarkdownImage({ src, alt, node: _node }) {
  const label = `Image${alt ? `: ${alt}` : ""}`;
  if (!src) return <span>[{label}]</span>;
  return <MarkdownLink href={src}>[{label}]</MarkdownLink>;
}

const MARKDOWN_COMPONENTS = { a: MarkdownLink, img: MarkdownImage };

/** Shared typography for assistant bubbles (streaming plain text and final markdown match). */
export const assistantTextSx = {
  fontSize: "0.875rem",
  lineHeight: 1.55,
  color: "var(--q-text, inherit)",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

/**
 * Message body: markdown for Inky, plain pre-wrapped text for the user.
 * @param {object} props - Component props.
 * @param {'user'|'assistant'} props.role - Message role.
 * @param {string} props.content - Message text.
 * @returns {JSX.Element} Body.
 */
const MessageMarkdown = memo(function MessageMarkdown({ role, content }) {
  if (role === "user") {
    return (
      <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" }}>
        {content}
      </Typography>
    );
  }
  return (
    <Box
      className="inky-md"
      sx={{
        ...assistantTextSx,
        "& p": { mt: 0, mb: 1 },
        "& p:last-child, & ul:last-child, & ol:last-child": { mb: 0 },
        "& ul, & ol": { mt: 0, mb: 1, pl: 2.5 },
        "& li": { mb: 0.25 },
        "& h1, & h2, & h3, & h4": { fontSize: "0.95rem", fontWeight: 600, mt: 1.5, mb: 0.5 },
        "& h1:first-of-type, & h2:first-of-type, & h3:first-of-type": { mt: 0 },
        "& code": {
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          bgcolor: "var(--q-subtle, rgba(0,0,0,0.06))",
          px: 0.5,
          py: 0.125,
          borderRadius: "var(--q-radius-xs, 4px)",
          fontSize: "0.85em",
        },
        "& pre": {
          m: 0,
          mb: 1,
          p: 1,
          bgcolor: "var(--q-subtle, rgba(0,0,0,0.06))",
          borderRadius: "var(--q-radius-md, 8px)",
          overflow: "auto",
          fontSize: "0.8rem",
        },
        "& pre code": { bgcolor: "transparent", p: 0 },
        "& blockquote": { m: 0, mb: 1, pl: 1.5, borderLeft: "3px solid var(--q-border, #E3E0DA)", color: "var(--q-muted, inherit)" },
        "& table": { borderCollapse: "collapse", mb: 1, fontSize: "0.8rem" },
        "& th, & td": { border: "1px solid var(--q-border, #E3E0DA)", px: 0.75, py: 0.25, textAlign: "left" },
        "& a": { wordBreak: "break-word" },
      }}
    >
      <ReactMarkdown components={MARKDOWN_COMPONENTS} urlTransform={inkyUrlTransform}>
        {content}
      </ReactMarkdown>
    </Box>
  );
});

export default MessageMarkdown;
